import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GEO_LOCATION } from 'utils/fieldTypes';
import { SMS_RECEIVED, USER } from 'constants/esDataTypes';
import { selectPresentState } from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import { BoldText } from 'modules/workflowNodeEditors/EditorBody.styled';
import { WORKFLOW_TRIGGERS } from 'modules/workflows';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { useFieldMappingsContext } from 'modules/workflows/context/FieldMappingsContext';
import {
  getAreMappingsPopulated,
  getClonedFromJourneyInvalidNodes,
  getEventFieldMappings,
  getIsQueryBuilderImprovementsEnabled,
  getIsFetchingEventFieldMappings,
  getIsJourneyFieldMappingsEnabled,
  getIsNodeEditorReadOnly,
  getIsYesNoSplitDataToggleEnabled,
  getIsYesNoSplitTestEnabled,
  getStartNodeTriggerEventName,
  getStartNodeTriggerNodeClass,
  updateNodeData,
} from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import {
  isStartTileUnfilterable,
  isUndeterminableOrigin,
  YesNoSplitEditor,
  FIELDS_MATCH_TOGGLE_IDS,
} from 'modules/workflowNodeEditors/components/YesNoSplitEditor';
// import { FieldMatchNodeData } from 'modules/workflows/types';
import { selectIsAllCombinatorSearchQueryFeatureFlagOn } from 'core/userContext';
import { CAMPAIGN } from 'utils/fieldConstants';
// import { EditorBaseProps } from '../../types';
import { StyledFormSectionContent } from './FieldsMatchEditor.styles';
import { FieldsMatchQueryBuilder } from './FieldsMatchQueryBuilder';
import { getFieldsMatchEditorNodeDataToBeCompared } from './FieldsMatchQueryBuilder.utils';
import { FieldsMatchRadio } from './FieldsMatchRadio';
import { FieldsMatchWarningBanner } from './FieldsMatchWarningBanner';
import TestSplitResultsButton from 'modules/workflowNodeEditors/components/YesNoSplitEditor/TestSplitResultsButton/TestSplitResultsButton';
import {
  DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY,
  DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY_WITH_ALL,
  FIELDS_MATCH_EDITOR_TEST_ID,
} from './constants';

// export type FieldsMatchEditorProps = EditorBaseProps<FieldMatchNodeData>;

export const FieldsMatchEditorCurrent = (
  { nodeData = {}, nodeId, clonedFromNodeData, node, ...rest } /*: FieldsMatchEditorProps */,
) => {
  const dispatch = useDispatch();

  // selectPresentState gets the up-to-date redux state for the QueryBuilder component
  // which is the exact data structure we need to update the node's "searchQuery" prop with.
  const searchQuery = useSelector(selectPresentState);

  /** Readonly state related variables */
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  /** Cloning */
  const invalidClonedNodes = useSelector(getClonedFromJourneyInvalidNodes);
  const hasCloningErrors = invalidClonedNodes.includes(nodeId);

  /** Event field mappings, needed for filters */
  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);
  const { fieldMappings, isLoading, didError } = useFieldMappingsContext();
  const mappingsOld = useSelector(getEventFieldMappings);
  const mappings = isJourneyFieldMappingsEnabled ? fieldMappings : mappingsOld;
  const isFetchingOld = useSelector(getIsFetchingEventFieldMappings);
  const isFetchingEventFieldMappings = isJourneyFieldMappingsEnabled ? isLoading : isFetchingOld;
  const areMappingsPopulated = useSelector(getAreMappingsPopulated);
  const areMappingsLoaded = isJourneyFieldMappingsEnabled
    ? !isLoading && !didError
    : areMappingsPopulated && !isFetchingEventFieldMappings;

  /** Yes/No Split Toggle */
  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);
  const isYesNoSplitTestEnabled = useSelector(getIsYesNoSplitTestEnabled);
  const startNodeClass = useSelector(getStartNodeTriggerNodeClass);
  const startNodeEventName = useSelector(getStartNodeTriggerEventName);
  const { matchTriggeringEventFields = null } = nodeData;

  // create a state value to track toggle value
  const [localMatchTriggeringEventFields, setLocalMatchTriggeringEventFields] = useState(
    matchTriggeringEventFields
      ? FIELDS_MATCH_TOGGLE_IDS.TRIGGERING_EVENT
      : FIELDS_MATCH_TOGGLE_IDS.CONTACT_PROFILE,
  );

  const isMatchTriggeringEventSelected =
    localMatchTriggeringEventFields === FIELDS_MATCH_TOGGLE_IDS.TRIGGERING_EVENT;

  const shouldMatchEvent = isYesNoSplitDataToggleEnabled && isMatchTriggeringEventSelected;
  const startNodeTriggersOnUnspecifiedCustomEvent =
    startNodeClass === WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT &&
    (!startNodeEventName || startNodeEventName.length === 0);
  const isWarningShown =
    shouldMatchEvent &&
    (isUndeterminableOrigin(startNodeClass) ||
      node.hasError ||
      startNodeTriggersOnUnspecifiedCustomEvent);
  const isQueryBuilderDisabled = shouldMatchEvent && isStartTileUnfilterable(startNodeClass);

  /** all/all combinator feature flag */
  const isAllEmptySearchQuery = useSelector(selectIsAllCombinatorSearchQueryFeatureFlagOn);

  /** Compare and Update node data */
  const getNodeDataForSaving = useCallback(
    (shouldReset = false) => {
      if (shouldReset) {
        const updatedSearchQuery = isAllEmptySearchQuery
          ? DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY
          : DEFAULT_FIELDS_MATCH_EMPTY_SEARCH_QUERY_WITH_ALL;

        return {
          searchQuery: updatedSearchQuery,
          matchTriggeringEventFields: !isStartTileUnfilterable(startNodeClass),
        };
      }
      return {
        searchQuery,
        matchTriggeringEventFields: isMatchTriggeringEventSelected,
      };
    },
    [isAllEmptySearchQuery, isMatchTriggeringEventSelected, searchQuery, startNodeClass],
  );

  const initialNodeData = useMemo(
    () => getFieldsMatchEditorNodeDataToBeCompared(nodeData),
    [nodeData],
  );

  const updatedNodeData = useMemo(() => {
    const data = getNodeDataForSaving();
    return getFieldsMatchEditorNodeDataToBeCompared(data);
  }, [getNodeDataForSaving]);

  const handleUpdate = useCallback(() => {
    const shouldReset =
      shouldMatchEvent && isEqual(initialNodeData, updatedNodeData) && node.hasError;
    const nodeDataForSaving = getNodeDataForSaving(shouldReset);

    dispatch(updateNodeData(nodeDataForSaving, nodeId));
  }, [
    dispatch,
    getNodeDataForSaving,
    initialNodeData,
    node?.hasError,
    nodeId,
    shouldMatchEvent,
    updatedNodeData,
  ]);

  return (
    <NodeEditor
      {...rest}
      dataTest={FIELDS_MATCH_EDITOR_TEST_ID}
      footerButtons={
        isYesNoSplitTestEnabled && rest.workflowData?.workflowType === WorkflowType.DRAFT ? (
          <TestSplitResultsButton
            searchQuery={searchQuery}
            node={node}
            nodeId={nodeId}
            workflowData={rest.workflowData}
          />
        ) : null
      }
      isLoadingIconShown={!areMappingsLoaded}
      node={node}
      onUpdate={handleUpdate}
      shouldFetchMappings
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
    >
      {isWarningShown && (
        <FieldsMatchWarningBanner
          startNodeClass={startNodeClass}
          startNodeTriggersOnUnspecifiedCustomEvent={startNodeTriggersOnUnspecifiedCustomEvent}
        />
      )}
      <StyledFormSectionContent>
        <FieldsMatchRadio
          isMatchTriggeringEventSelected={isMatchTriggeringEventSelected}
          onRadioUpdate={setLocalMatchTriggeringEventFields}
          radioValue={localMatchTriggeringEventFields}
        />
        {areMappingsLoaded && (
          <>
            <BoldText>Split criteria</BoldText>
            <FieldsMatchQueryBuilder
              nodeData={nodeData}
              clonedFromNodeData={isEmpty(clonedFromNodeData) ? undefined : clonedFromNodeData}
              fieldMappings={mappings}
              isMatchTriggeringEventFields={isMatchTriggeringEventSelected}
              hasCloningErrors={hasCloningErrors}
              isReadOnly={isTileEditorReadOnly || isQueryBuilderDisabled}
              filteredFieldsByPropertyMap={{
                [SMS_RECEIVED]: { value: CAMPAIGN },
                [USER]: { type: GEO_LOCATION },
              }}
            />
          </>
        )}
      </StyledFormSectionContent>
    </NodeEditor>
  );
};

export const FieldsMatchEditor = (props) => {
  const isQueryBuilderImprovementsEnabled = useSelector(getIsQueryBuilderImprovementsEnabled);

  return isQueryBuilderImprovementsEnabled ? (
    <YesNoSplitEditor {...props} />
  ) : (
    <FieldsMatchEditorCurrent {...props} />
  );
};
